import type Types from '@aeppic/types'

import type { ConditionEvaluation } from './conditions.js'
import type { CommandAeppicInterface } from './executor.js'

export interface CommandCheck {
  passed: boolean
  reasons: string[]
  conditions: ConditionEvaluation[]
}

export interface CommandAvailability {
  command: Types.Reference
  kind: 'command' | 'group'
  visible: boolean
  enabled: boolean
  hasRight: boolean
  available: boolean
  checks: {
    visibility: CommandCheck
    enabled: CommandCheck
    rights: CommandCheck
  }
  children?: CommandAvailability[]
}

export interface DocumentCommands {
  target: Types.Reference
  commands: CommandAvailability[]
}

export function commandReference(document: Types.Document): Types.Reference {
  return {
    id: document.id,
    v: document.v,
    text: String(document.data?.label || document.data?.name || document.id),
  }
}

export function commandCheck(passed: boolean, reasons: string[] = [], conditions: ConditionEvaluation[] = []): CommandCheck {
  return { passed, reasons, conditions }
}

export function conditionCheck(conditions: ConditionEvaluation[]): CommandCheck {
  const failed = conditions.filter(condition => !condition.passed)
  const reasons = failed.map(condition => condition.reason || condition.error || condition.name)
  return commandCheck(failed.length === 0, reasons, conditions)
}

/** One invocation owns its reads and evaluations; no availability survives a later call. */
export class DocumentCommandLookup {
  private documents = new Map<string, Promise<Types.Document>>()
  private evaluations = new Map<string, Promise<CommandAvailability>>()

  constructor(
    private api: CommandAeppicInterface,
    private evaluate: (command: Types.Document) => Promise<CommandAvailability>,
  ) {}

  async resolve(target: Types.Document): Promise<DocumentCommands> {
    const form = await this.formForCommands(target)
    if (!form) {
      throw Object.assign(new Error('Command target form not found'), { status: 404 })
    }

    const commands = await this.resolveReferences(form.document.data.commands as Types.Reference[] || [], new Set())
    return { target: commandReference(target), commands }
  }

  /** Command lists follow the current form, not the version pinned on the document. */
  private async formForCommands(target: Types.Document) {
    const formId = target?.f?.id
    if (formId && typeof this.api.getForm === 'function') {
      const latest = await this.api.getForm(formId)
      if (latest?.document) return latest
    }
    return this.api.getFormForDocument(target)
  }

  private async resolveReferences(references: Types.Reference[], ancestors: Set<string>): Promise<CommandAvailability[]> {
    const entries = await Promise.all(references.map(reference => this.resolveReference(reference, ancestors)))
    return entries.filter(entry => entry !== null)
  }

  private async resolveReference(reference: Types.Reference, ancestors: Set<string>): Promise<CommandAvailability | null> {
    if (ancestors.has(reference.id)) {
      throw new Error(`Circular command group: ${reference.id}`)
    }

    // Command definitions follow the same reference/version rules as ordinary get calls.
    const key = JSON.stringify([reference.id, reference.v])
    if (!this.documents.has(key)) {
      this.documents.set(key, this.api.get(reference))
    }
    const command = await this.documents.get(key)
    if (!command) {
      return null
    }
    if (command.f.id !== 'command-form' && command.f.id !== 'command-group-form') {
      return null
    }

    if (!this.evaluations.has(key)) {
      this.evaluations.set(key, this.evaluate(command))
    }
    const evaluation = this.evaluations.get(key)
    if (command.f.id !== 'command-group-form') {
      return evaluation
    }

    const childAncestors = new Set(ancestors).add(command.id)
    const [group, children] = await Promise.all([
      evaluation,
      this.resolveReferences(command.data.commands as Types.Reference[] || [], childAncestors),
    ])
    const inheritedChildren = children.map(child => this.inheritGroupChecks(child, group))

    const hasAvailableChildren = inheritedChildren.some(child => child.available)
    const checks = { ...group.checks }
    if (group.enabled && !hasAvailableChildren) {
      checks.enabled = commandCheck(false, ['No available commands in this group'], group.checks.enabled.conditions)
    }

    return {
      ...group,
      enabled: checks.enabled.passed,
      available: group.available && hasAvailableChildren,
      checks,
      children: inheritedChildren,
    }
  }

  private inheritGroupChecks(child: CommandAvailability, group: CommandAvailability): CommandAvailability {
    const visible = group.visible && child.visible
    const enabled = group.enabled && child.enabled
    const checks = { ...child.checks }

    for (const key of ['visibility', 'enabled'] as const) {
      if (!group.checks[key].passed) {
        checks[key] = commandCheck(false, [...group.checks[key].reasons, ...child.checks[key].reasons], child.checks[key].conditions)
      }
    }

    return {
      ...child,
      visible,
      enabled,
      available: visible && enabled && child.hasRight && child.available,
      checks,
      ...(child.children ? { children: child.children.map(entry => this.inheritGroupChecks(entry, group)) } : {}),
    }
  }
}
